import { useEffect, useState } from "react";
import DeleteIcon from "../../../components/Icons/DeleteIcon";
import SearchSelectInput from "../../../components/comman/SearchSelectInput";
import { useWatch, useFieldArray } from "react-hook-form";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { useLocation } from "react-router-dom";
import BudgetRequestItemRow from "./BudgetRequestItemRow";
import PlusIcon from "../../../components/Icons/PlusIcon";
import { parseAmount, formatAmount } from "../../../utility/CommonFunctions";

const BudgetExpenseHead = ({
    control,
    register,
    sectionIndex,
    headIndex,
    removeHead,
    errors,
    setValue,
    usedExpenseHeads,
    requestType,
    siteId,
    activeFinancialYear,
    isEditMode
}) => {
    const expenseHeadId = useWatch({
        control,
        name: `sections.${sectionIndex}.expenseHeads.${headIndex}.expenseHeadId`,
    });

    const items = useWatch({
        control,
        name: `sections.${sectionIndex}.expenseHeads.${headIndex}.items`,
    });

    const { fields, append, remove, replace } = useFieldArray({
        control,
        name: `sections.${sectionIndex}.expenseHeads.${headIndex}.items`,
    });

    const location = useLocation();
    const isDelete = location?.state?.isDelete;

    const [expenseHeadList, setExpenseHeadList] = useState([]);
    const [isHeadLoading, setIsHeadLoading] = useState(false);
    const [selectedHeadName, setSelectedHeadName] = useState("");

    const fetchExpenseHeads = (search = "") => {
        if (!requestType || !siteId) return;
        if ((isEditMode || isDelete) && !activeFinancialYear) return;

        setIsHeadLoading(true);
        let API = "";
        let options = { method: "GET" };

        if (isEditMode || isDelete) {
            let typeParam = "serviceBudget";
            if (requestType === "Supply") typeParam = "supplyBudget";
            else if (requestType === "Staff") typeParam = "staffBudget";
            else if (requestType === "General") typeParam = "generalBudget";

            API = `${apiUrl.get_site_budget}type=${typeParam}&siteId=${siteId}&financialYear=${activeFinancialYear}&search=${search}`;
        } else {
            if (requestType === "Staff") {
                API = `${apiUrl.get_department}?search=${search}&type=site`;
            } else {
                let type = "Service";
                if (requestType === "Supply") type = "Supply";
                else if (requestType === "General") type = "General";

                API = `${apiUrl.get_expense_head_budget_request}?siteId=${siteId}&type=${type}&financialYear=${activeFinancialYear}&search=${search}`;
            }
        }

        callApi(API, options).then(async (res) => {
            setIsHeadLoading(false);
            if (res?.status) {
                let data = res?.data || [];
                if (requestType === "Staff") {
                    const items = res?.data?.department || [];
                    if (isEditMode || isDelete) {
                        data = data?.map(item => ({
                            ...item,
                            label: item?.department?.name || item?.expensehead?.name,
                            value: item?.department?.id || item?.expensehead?.id,
                            disabled: usedExpenseHeads.includes(item?.department?.id || item?.expensehead?.id) && expenseHeadId !== (item?.department?.id || item?.expensehead?.id)
                        }));
                    } else {
                        data = items.map(item => ({
                            ...item,
                            id: item.id,
                            label: item.name,
                            value: item.id,
                            disabled: (usedExpenseHeads.includes(item.id) && expenseHeadId !== item.id),
                        })) || [];
                    }
                } else {
                    if (isEditMode || isDelete) {
                        data = data?.map(item => ({
                            ...item,
                            label: item?.expensehead?.name,
                            value: item?.expensehead?.id,
                            disabled: usedExpenseHeads.includes(item?.expensehead?.id) && expenseHeadId !== (item?.expensehead?.id)
                        }));
                    } else {
                        // Patch for backend bug where alreadyUsed is true for deleted items
                        let typeParam = "serviceBudget";
                        if (requestType === "Supply") typeParam = "supplyBudget";
                        else if (requestType === "General") typeParam = "generalBudget";
                        
                        let siteBudgetAPI = `${apiUrl.get_site_budget}type=${typeParam}&siteId=${siteId}&financialYear=${activeFinancialYear}`;
                        let budgetRes = await callApi(siteBudgetAPI, { method: "GET" });
                        
                        let activeExpenseHeadIds = [];
                        if (budgetRes?.status) {
                            activeExpenseHeadIds = budgetRes.data.map(b => b.expensehead?.id || b.expenseheadId || b.id);
                        }

                        data = data?.map(item => ({
                            ...item,
                            id: item.id,
                            label: item.name,
                            value: item.id,
                            disabled: activeExpenseHeadIds.includes(item.id) || (usedExpenseHeads.includes(item.id) && expenseHeadId !== item.id),
                        }));
                    }
                }
                setExpenseHeadList(data);
            } else {
                setExpenseHeadList([]);
            }
        });
    };

    // Initial fetch on mount or dep change
    useEffect(() => {
        fetchExpenseHeads();
    }, [requestType, siteId, activeFinancialYear, isEditMode, isDelete]);

    // Update selected name when ID changes
    useEffect(() => {
        if (expenseHeadId && expenseHeadList.length > 0) {
            const selected = expenseHeadList.find(h => h.value == expenseHeadId);
            if (selected) setSelectedHeadName(selected.label);
        } else if (!expenseHeadId) {
            setSelectedHeadName("");
        }
    }, [expenseHeadId, expenseHeadList]);

    const [itemsList, setItemsList] = useState([]);
    useEffect(() => {
        if (expenseHeadId && expenseHeadList.length > 0) {
            const selectedHead = expenseHeadList.find((head) => head.value == expenseHeadId);
            if (requestType === "Staff") {
                // Fetch designations dynamically for the selected department
                const fetchDesignations = async () => {
                    let API = apiUrl?.get_designation + `?departmentId=${expenseHeadId}`;
                    let res = await callApi(API, { method: "GET" });
                    let designations = res?.data?.designation || [];
                    
                    if (!isEditMode && !isDelete && siteId && activeFinancialYear) {
                        let siteBudgetAPI = `${apiUrl.get_site_budget}type=staffBudget&siteId=${siteId}&financialYear=${activeFinancialYear}`;
                        let budgetRes = await callApi(siteBudgetAPI, { method: "GET" });
                        
                        if (budgetRes?.status) {
                            const existingBudget = budgetRes?.data?.find(b => b.department?.id == expenseHeadId || b.departmentId == expenseHeadId);
                            if (existingBudget && existingBudget.sitebudgetitems) {
                                const usedIds = existingBudget.sitebudgetitems.map(item => item.designationId || item.itemId || item.id);
                                designations = designations.map(d => ({
                                    ...d,
                                    alreadyUsed: usedIds.includes(d.id)
                                }));
                            }
                        }
                    }
                    
                    setItemsList(designations);
                };
                fetchDesignations();
            } else {
                setItemsList(selectedHead?.sitebudgetitems || []);
            }
        } else {
            setItemsList([]);
        }
    }, [expenseHeadId, expenseHeadList, requestType]);

    const handleItemChange = (itemIndex, itemId) => {
        const selectedItem = itemsList.find((i) => i.itemId === itemId || i.id === itemId);
        if (selectedItem) {
            setValue(`sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.hsnCode`, selectedItem?.item?.hsnCode || "");
            setValue(`sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.availableQty`, (+selectedItem?.quantity || 0) - (+selectedItem?.freezedQuantity || 0));
        }
    };

    const handleExpenseHeadChange = (option) => {
        setValue(`sections.${sectionIndex}.expenseHeads.${headIndex}.expenseHeadId`, option.value);
        const items = expenseHeadList?.find((item) => item.value == option.value);
        
        if (isEditMode || isDelete) {
            setValue(`sections.${sectionIndex}.expenseHeads.${headIndex}.siteBudgetId`, items?.id);
            if (isDelete) {
                setValue(`sections.${sectionIndex}.expenseHeads.${headIndex}.expectedAmount`, items?.expectedAmount || 0);
            }
            
            if (items?.sitebudgetitems && items.sitebudgetitems.length > 0) {
                const existingItems = items.sitebudgetitems.map(item => ({
                    itemId: item?.designationId || item?.itemId,
                    siteBudgetItemId: item?.id,
                    hsnCode: item?.item?.hsnCode,
                    quantity: item?.quantity ?? "",
                    rate: item?.unitRate ?? item?.rate ?? "",
                    gst: item?.gst ?? "0",
                    month: item?.month ?? "",
                    amount: item?.total ?? item?.amount ?? "",
                    originalMonth: item?.month,
                    originalAmount: item?.amount || item?.total,
                    originalRate: item?.rate || item?.unitRate,
                    originalGst: item?.gst,
                    availableQty: (item?.freezedQuantity || 0)
                }));
                replace(existingItems);
            } else {
                replace([]);
            }
        } else {
            if (isStaffBudget) replace([]);
        }
    };

    const isStaffBudget = requestType === "Staff";
    const headLabel = isStaffBudget ? "Department" : "Expense Head";

    return (
        <div className="border rounded px-3 pt-3 mb-3 bg-light">
            <div className="row align-items-start">
                <div className="col-12 col-md-3">
                    <div className="form-group mb-3">
                        <SearchSelectInput
                            label={headLabel}
                            placeholder={`Select ${headLabel}`}
                            options={expenseHeadList}
                            value={selectedHeadName}
                            onSearch={fetchExpenseHeads}
                            onSelect={handleExpenseHeadChange}
                            isLoading={isHeadLoading}
                            required={true}
                            errors={errors?.sections?.[sectionIndex]?.expenseHeads?.[headIndex]?.expenseHeadId?.message}
                            disabled={siteId === ""}
                        />
                    </div>
                </div>

                {!isStaffBudget && (
                    <div className="col-12 col-md-3">
                        <div className="form-group mb-3">
                            <label className="form-label">Expected Amount <span className="text-danger">*</span></label>
                            <input
                                type="number"
                                className="form-control"
                                placeholder="Enter expected amount"
                                {...register(`sections.${sectionIndex}.expenseHeads.${headIndex}.expectedAmount`, {
                                    required: isDelete ? false : "Amount is required"
                                })}
                                disabled={isDelete}
                            />
                            {errors?.sections?.[sectionIndex]?.expenseHeads?.[headIndex]?.expectedAmount && (
                                <span className="text-danger small">{errors.sections[sectionIndex].expenseHeads[headIndex].expectedAmount.message}</span>
                            )}
                        </div>
                    </div>
                )}

                <div className="col-12 col-md-4">
                    <div className="form-group mb-3">
                        <label className="form-label">Description</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter description"
                            {...register(`sections.${sectionIndex}.expenseHeads.${headIndex}.description`)}
                            disabled={isDelete}
                        />
                        {errors?.sections?.[sectionIndex]?.expenseHeads?.[headIndex]?.description && (
                            <span className="text-danger small">{errors.sections[sectionIndex].expenseHeads[headIndex].description.message}</span>
                        )}
                    </div>
                </div>

                <div className="col-12 col-md-2">
                    <div className="form-group mb-3">
                        <label className="form-label d-none d-md-block">&nbsp;</label>
                        <button
                            type="button"
                            className="btn btn-outline-danger w-100 py-2"
                            onClick={() => removeHead(headIndex)}
                        >
                            <DeleteIcon /> Remove
                        </button>
                    </div>
                </div>
            </div>

            {isStaffBudget && (
                <>
                    <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                        <h4 className="mb-0">Item Details</h4>
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => append({ itemId: "", quantity: "", rate: "", gst: "", amount: "", month: "" })}
                            disabled={!expenseHeadId || isDelete}
                        >
                            <PlusIcon /> {isDelete ? "Insert" : "Add"} Item {isDelete ? " to Delete" : ""}
                        </button>
                    </div>

                    <div className="table-responsive bg-white rounded">
                        <table className="table table-bordered mb-0">
                            <thead>
                                <tr>
                                    <th style={{ width: "40%" }}>Designation</th>
                                    <th style={{ width: "12%" }}>Quantity</th>
                                    <th style={{ width: "10%" }}>Month</th>
                                    <th style={{ width: "12%" }}>Rate</th>
                                    <th style={{ width: "12%" }}>Amount</th>
                                    <th style={{ width: "10%" }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {fields.map((item, itemIndex) => (
                                    <BudgetRequestItemRow
                                        key={item.id}
                                        control={control}
                                        register={register}
                                        sectionIndex={sectionIndex}
                                        headIndex={headIndex}
                                        itemIndex={itemIndex}
                                        remove={remove}
                                        itemsList={itemsList}
                                        handleItemChange={handleItemChange}
                                        errors={errors}
                                        usedItems={items?.map((i) => i.itemId) || []}
                                        itemLabel="Designation"
                                        isStaffBudget={true}
                                        isServiceRequest={false}
                                        setValue={setValue}
                                        isEditMode={isEditMode}
                                        isOldData={item?.isOldData}
                                        activeItem={{
                                            rate: item?.originalRate || 0,
                                            gst: item?.originalGst || 0,
                                            month: item?.originalMonth || 0
                                        }}
                                    />
                                ))}
                                {!expenseHeadId ? (
                                    <tr>
                                        <td colSpan="6" className="text-center text-muted">
                                            Please select a department to add items.
                                        </td>
                                    </tr>
                                ) : fields.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center text-muted">
                                            No items added.
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>

                    <TotalAmountDisplay
                        control={control}
                        sectionIndex={sectionIndex}
                        headIndex={headIndex}
                        isEditMode={isEditMode}
                    />
                </>
            )}
        </div>
    );
};

const TotalAmountDisplay = ({ control, sectionIndex, headIndex, isEditMode }) => {
    const items = useWatch({
        control,
        name: `sections.${sectionIndex}.expenseHeads.${headIndex}.items`,
    });

    const total = (items || []).reduce((acc, item) => {
        return acc + (isEditMode ? parseAmount(item.amount) : parseAmount(item.amount) || 0);
    }, 0);

    return (
        <div className="d-flex justify-content-end mt-2">
            <h5 className="mb-0">Total Amount: {formatAmount(total)}</h5>
        </div>
    );
};

export default BudgetExpenseHead;
